"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import {ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import sideImg from "@/assets/students/student8.webp";
import Button from "../button/Button";



const ProgramsSection = ({ data, loading }) => {
  // Media URL helper
  const getMediaUrl = (media) => {
    const baseUrl = process.env.NEXT_PUBLIC_STRAPI_URL || "http://localhost:1337";
    const url = media?.url || media?.data?.attributes?.url || media?.data?.url;
    if (!url) return null;
    if (url.startsWith("http")) return url;
    return `${baseUrl}${url}`;
  };

  // Rich Text helper for Strapi Blocks
  const renderRichText = (blocks) => {
    if (typeof blocks === 'string') return blocks;
    if (Array.isArray(blocks)) {
      return blocks.map(block => {
        if (block.type === 'paragraph') {
          return block.children.map(child => child.text).join('');
        }
        return '';
      }).join('\n');
    }
    return '';
  };

  // Map Strapi data to component format
  const formattedData = data?.Tabs?.map(tab => ({
    cat: tab.Tab_title,
    categoryImages: tab.Single_card?.map(card => ({
      title: card.Card_title,
      desc: renderRichText(card.Card_description),
      img: getMediaUrl(card.Card_image) || sideImg,
      link: card.Card_url || "#",
    })) || []
  })) || [];

  const [activeTab, setActiveTab] = useState("");
  // CTA Data
  const ctaData = data;
  // Set initial active tab
  useEffect(() => {
    if (formattedData.length > 0 && !activeTab) {
      setActiveTab(formattedData[0].cat);
    }
  }, [formattedData, activeTab]);

  const activeData = formattedData.find((item) => item.cat === activeTab);
  const totalSlides = activeData?.categoryImages.length || 0;

  const getDesktopSlides = () => {
    if (totalSlides <= 3) return totalSlides;
    return 3.2;
  };

  if (loading && !data) {
    return (
      <div className="w-full h-96 bg-gray-50 animate-pulse flex items-center justify-center">
        <p className="text-gray-400">Loading Programmes...</p>
      </div>
    );
  }

  return (
    <div className="w-full mx-auto top_bottom_space">
      <div className="w-full bg-[#FDF7F2]">
        <div className="max-w-[var(--max-page-width)] mx-auto px-[var(--container-padding)] heading_space_bottom mb-4">
          <div className="w-full inner_top_bottom_space">
            <div className="grid grid-cols-1 lg:grid-cols-2 items-start gap-6 md:gap-9 lg:gap-12 mb-6 md:mb-14">
              <div className="flex flex-col gap-6">
                <div className="w-full">
                  <h2 className="text-[20px] md:text-[30px] lg:text-[38px] font-saansBold text-[#081E2D] mb-3">
                    {data?.Title || "Skill-Integrated Programmes for Real-World Success"}
                  </h2>
                  <p className="text-gray-700 text-[16px] md:text-[18px] mb-6 whitespace-pre-wrap">
                    {renderRichText(data?.Description) || "Our programmes are offered across diverse academic schools, combining strong academics with hands-on, industry-linked learning. Designed for today's careers, they help students graduate with recognised degrees, practical skills, and the confidence to step into the professional world."}
                  </p>
                </div>

                <div className="flex flex-wrap">
                  {formattedData.map((item) => (
                    <button
                      key={item.cat}
                      onClick={() => setActiveTab(item.cat)}
                      className={`cursor-pointer px-5 md:px-8 py-2 text-[14px] md:text-[18px] border transition-all ${activeTab === item.cat
                        ? "bg-[#081E2D] text-white border-[#081E2D]"
                        : "bg-white text-[#081E2D] border-gray-300 hover:bg-[#081E2D]/10"
                        }`}
                    >
                      {item.cat}
                    </button>
                  ))}
                </div>
              </div>

              <div className="hidden lg:flex justify-center md:justify-end grow">
                <div className="relative w-full h-full min-h-[400px]">
                  <Image
                    src={sideImg}
                    alt="Side Banner"
                    fill
                    loading="lazy"
                    decoding="async"
                    className="object-cover w-full h-full shadow-md"
                    // priority
                  />
                </div>
              </div>
            </div>

            <div className="relative w-full">
              {/* Desktop Navigation Buttons - Side positioned */}
              <button className="cursor-pointer swiper-button-prev-card absolute  left-0 top-1/2 -translate-y-1/2 z-10
         bg-black/60 hover:bg-black/80 text-white rounded-full w-10 h-10 shadow hidden md:flex items-center justify-center">
                {/* <span className="material-symbols-outlined leading-none align-middle">chevron_left</span> */}
              <ChevronLeft className="w-5 h-5 text-gray-200" />
              </button>

              <button className="cursor-pointer swiper-button-next-card absolute  right-0 top-1/2 -translate-y-1/2 z-10
         bg-black/60 hover:bg-black/80 text-white rounded-full w-10 h-10 shadow hidden md:flex items-center justify-center">
                {/* <span className="material-symbols-outlined leading-none align-middle">chevron_right</span> */}
              <ChevronRight className="w-5 h-5 text-gray-200" />
              </button>

              <Swiper
                key={activeTab}
                modules={[Autoplay, Navigation]}
                navigation={{
                  nextEl: ".swiper-button-next-card",
                  prevEl: ".swiper-button-prev-card",
                }}
                spaceBetween={20}
                loop={true}
                autoplay={{
                  delay: 4000,
                  disableOnInteraction: false,
                }}
                breakpoints={{
                  320: { slidesPerView: 1.1 },
                  640: { slidesPerView: 1.3 },
                  768: { slidesPerView: 2.3 },
                  992: { slidesPerView: 2.8 },
                  1024: { slidesPerView: 3.1 },
                  1280: { slidesPerView: getDesktopSlides() },
                }}
              >
                {activeData?.categoryImages.map((card, index) => (
                  <SwiperSlide key={index}>
                    <div
                      className="bg-white shadow-sm overflow-hidden hover:shadow-lg transition-transform
                        hover:-translate-y-1 h-[500px] md:h-[600px] flex flex-col max-w-[450px] mx-auto"
                    >
                      <div className="hover:bg-[#FFA201] transition-all duration-300 px-5 py-8 md:py-10 
                        flex flex-col justify-between h-[300px] md:h-[350px]">
                        <div className="py-4">
                          <p className="font-bold text-[20px] md:text-[22px] lg:text-[24px] mb-2">
                            <Link href={card.link}>
                              {card.title}
                            </Link>
                          </p>
                          <p className="text-[16px] md:text-[18px]">
                            {card.desc}
                          </p>
                        </div>


                        <p
                          onClick={() => window.location.href = card.link}
                          className="text-[16px] font-semibold flex items-center gap-1 cursor-pointer"
                        >
                          <span>Learn More</span>
                          <ArrowRight size={20} />
                        </p>


                      </div>

                      <div className="relative w-full h-[400px]">
                        <Image
                          src={card.img}
                          alt={card.title}
                          fill
                          className="object-cover object-center"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>

              {/* Mobile Navigation Buttons - Bottom center positioned */}
              <div className="flex md:hidden justify-center items-center gap-4 mt-6">
                <button className="cursor-pointer swiper-button-prev-card 
           bg-black/60 hover:bg-black/80 text-white rounded-full w-10 h-10 shadow flex items-center justify-center">
                  {/* <span className="material-symbols-outlined leading-none align-middle">chevron_left</span> */}
                <ChevronLeft className="w-5 h-5 text-gray-200" />
                </button>

                <button className="cursor-pointer swiper-button-next-card 
           bg-black/60 hover:bg-black/80 text-white rounded-full w-10 h-10 shadow flex items-center justify-center">
                  {/* <span className="material-symbols-outlined leading-none align-middle">chevron_right</span> */}
                  <ChevronRight className="w-5 h-5 text-gray-200" />
                </button>
              </div>
            </div>
          </div>
          {data?.CTA?.Show_cta && (
            <div className='w-full flex bg-[#FFA201] py-0 md:py-6 px-4 md:px-7'>
              <Button
                text={data?.CTA?.CTA_button_text || "Apply Now"}
                link={data?.CTA?.cta_button_url || "https://application.msu.edu.in/campus-programme-registration"}
                paragraph={data?.CTA?.cta_text || "Start Your Journey Towards a Future-Ready Career With Us"}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProgramsSection;
